import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  Checkbox,
  Col,
  Form,
  FormInstance,
  Input,
  Radio,
  Row,
  Select
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import cs from 'classnames';
import AuthService from 'services/authService';

import CountdownTimer from 'components/CountdownTimer/CountdownTimer';
import CountryPreview from 'components/CountryPreview/CountryPreview';
import DialogModal from 'components/DialogModal/DialogModal';
import PhoneSelect from 'components/PhoneSelect/PhoneSelect';
import SelectLanguageLevels from 'components/SelectLanguageLevels/SelectLanguageLevels';
import StaticNotification from 'components/StaticNotification/StaticNotification';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import useCountryCitySelect from 'hooks/useCountryCitySelect';
import useErrorCatch from 'hooks/useErrorCatch';

import formatAndOrderData from 'utils/formatAndOrderData';
import Services from 'utils/services';

import { Country } from 'types/commons';

import allCountries from 'constants/allCountries';
import { SECTION_ROW_GUTTER } from 'constants/commons';
import {
  DialogModalTypes,
  Dictionaries,
  IntakeFormModalType
} from 'constants/enums';
import { decisionBooleanArray } from 'constants/formData';
import { PRIVACY_POLICY } from 'constants/path';

import { ReactComponent as ArrowRight } from 'assets/icons/arrowRight.svg';

import styles from './IntakeForm.module.scss';
import SkillsetSelect from './SkillsetSelect';

type IntakeFormProps = {
  form: FormInstance;
  onCancelClick: () => void;
  onSendRegistrationForm: (location: string) => void;
  isLoading: boolean;
};

const IntakeForm: FC<IntakeFormProps> = ({
  form,
  onCancelClick,
  onSendRegistrationForm,
  isLoading
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [isCountriesLoading, setIsCountriesLoading] = useState<boolean>(true);
  const [isPrivacyAndHealthChecked, setIsPrivacyAndHealthChecked] =
    useState<boolean>(false);
  const [isSMSConsentChecked, setIsSMSConsentChecked] =
    useState<boolean>(false);
  const [isHealthModalOpen, setIsHealthModalOpen] = useState<boolean>(false);
  const [isSMSConsentModalOpen, setIsSMSConsentModalOpen] =
    useState<boolean>(false);
  const [isTimerActive, setIsTimerActive] = useState(false);

  const COUNTRIES_ORDER = ['USA', 'Canada'];

  const catchError = useErrorCatch();

  const jobShiftDict = useSelector(selectDictionary(Dictionaries.JobShift));

  const { countrySelect, citySelect, rawString } = useCountryCitySelect({
    form,
    countries,
    isCountriesLoading,
    isCountryRequired: true,
    isCityRequired: true
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await Services.getCountries();
        setCountries(res);
      } catch (error) {
        catchError({
          error,
          default: {
            message: 'Operation Failed',
            description: 'Failed to fetch Counties'
          }
        });
      } finally {
        setIsCountriesLoading(false);
      }
    };

    fetchCountries();
  }, []);

  const industries = useSelector(selectDictionary(Dictionaries.Industry));

  const options = allCountries.map(({ code, name }) => ({
    value: code,
    label: name
  }));

  const sortedOptions = formatAndOrderData(options, COUNTRIES_ORDER);

  const countriesOptions = sortedOptions.map(({ value, label }) => ({
    label: <CountryPreview countryCode={value} countryName={label} />,
    value: label
  }));

  const { email } = form.getFieldsValue(['email']);

  const message = (
    <>
      A verification code has been sent to <b>{email}</b>. Please enter the code
      below to continue.
    </>
  );

  const requestCode = async (): Promise<void> => {
    try {
      await AuthService.requestVerificationCode({ email });
      setIsTimerActive(true);
    } catch (error) {
      catchError({
        error,
        default: {
          message:
            "Can't send verification code, please check you filled email correctly"
        }
      });
    }
  };

  const handleChangePrivacyAndHealthCheckbox = (
    e: CheckboxChangeEvent
  ): void => {
    setIsPrivacyAndHealthChecked(e.target.checked);
  };

  const handleClickSMSConsentCheckbox = (): void => {
    setIsSMSConsentChecked((prev) => !prev);
  };

  const handleFormSubmit = async () => {
    try {
      await form.validateFields();

      onSendRegistrationForm(rawString);
    } catch (error) {
      catchError({
        error,
        default: {
          message: 'Validation failed'
        }
      });
    }
  };

  const toggleModalOpen =
    (modalType: IntakeFormModalType, value: boolean) => () => {
      if (modalType === IntakeFormModalType.Health) {
        setIsHealthModalOpen(value);
        return;
      }
      setIsSMSConsentModalOpen(value);
    };

  const handleSMSConsentModalConfirm = () => {
    setIsSMSConsentChecked(true);
    setIsSMSConsentModalOpen(false);
  };

  useEffect(() => {
    if (email) {
      requestCode();
    }
  }, [email]);

  return (
    <>
      <StaticNotification text={message} />
      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col span={24}>
          <Form.Item
            name="code"
            label="Verification code"
            rules={[
              {
                required: true,
                message: 'Enter verification code'
              }
            ]}
          >
            <Input placeholder="______" maxLength={6} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col md={12} xs={24}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true }]}
          >
            <Input placeholder="Jon" />
          </Form.Item>
        </Col>
        <Col md={12} xs={24}>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true }]}
          >
            <Input placeholder="Smith" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col md={12} xs={24}>
          {countrySelect}
        </Col>
        <Col md={12} xs={24}>
          {citySelect}
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col md={12} xs={24}>
          <Form.Item name="mailingAddress" label="Home Address">
            <Input placeholder="This helps us find local jobs and assist with relocation" />
          </Form.Item>
        </Col>
        <Col md={12} xs={24}>
          <Form.Item
            name="citizenship"
            label="Nationality (Country)"
            rules={[{ required: true, message: 'Please select Country!' }]}
            className={styles.field}
          >
            <Select
              placeholder="Select your country of citizenship"
              options={countriesOptions}
              disabled={isLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col md={12} xs={24}>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[{ required: true }]}
          >
            <PhoneSelect
              name="phoneNumber"
              form={form}
              className="phone-select"
            />
          </Form.Item>
        </Col>
        <Col md={12} xs={24}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                type: 'email',
                required: true,
                message: 'email is not a valid'
              }
            ]}
          >
            <Input placeholder="Enter Your Email" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col md={12} xs={24}>
          <SelectLanguageLevels
            form={form}
            name="languages"
            label="Spoken Languages"
            placeholder="Tell us what languages you speak"
          />
        </Col>
        <Col md={12} xs={24}>
          <Form.Item
            name="skills"
            label="What are your skills?"
            rules={[{ required: true }]}
          >
            <SkillsetSelect placeholder="List your skills to help us find the right job for you" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col span={24}>
          <Form.Item
            name="lookingForJobInAreas"
            label="Select your preferred industries"
            rules={[{ required: true, message: 'Please select Industry!' }]}
          >
            <Select
              placeholder="What industries are you looking to work In?"
              mode="multiple"
              className="item-select"
              maxTagCount="responsive"
              options={industries}
              disabled={isLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col md={12} xs={24}>
          <div className={styles.label}>
            Are you open to work weekends or overtime, if required? *
          </div>
          <Form.Item
            name="weekendOvertime"
            rules={[{ required: true, message: 'Please select any value' }]}
          >
            <Radio.Group
              options={decisionBooleanArray}
              className="vertical-list"
            />
          </Form.Item>
        </Col>
        <Col md={12} xs={24}>
          <div className={styles.label}>
            Select a shift that fits your schedule *
          </div>
          <Form.Item
            name="preferredSchedule"
            rules={[{ required: true, message: 'Please select any value' }]}
          >
            <Checkbox.Group
              options={jobShiftDict}
              className="checkbox-group-vertical"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col md={12} xs={24}>
          <div className={styles.label}>
            Have you ever been convicted of a felony *
          </div>
          <Form.Item
            name="felonyConviction"
            rules={[{ required: true, message: 'Please select any value' }]}
          >
            <Radio.Group
              options={decisionBooleanArray}
              className="vertical-list"
            />
          </Form.Item>
        </Col>
        <Col md={12} xs={24}>
          <div className={styles.label}>
            Are you willing to undergo a background check as part of the hiring
            process, if required? *
          </div>
          <Form.Item
            name="backgroundCheckConsent"
            rules={[{ required: true, message: 'Please select any value' }]}
          >
            <Radio.Group
              options={decisionBooleanArray}
              className="vertical-list"
            />
          </Form.Item>
        </Col>
      </Row>

      <StaticNotification text="By submitting this form, you consent to the creation of an account on your behalf. Your profile will be shared with our partner employers as part of the recruitment process." />

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col span={24}>
          <Form.Item rules={[{ required: true }]}>
            <Checkbox
              onChange={handleChangePrivacyAndHealthCheckbox}
              className={cs(
                {
                  [styles.checkboxChecked]: isPrivacyAndHealthChecked
                },
                styles.checkbox
              )}
            >
              Have you read and agreed with our{' '}
              <a href={PRIVACY_POLICY} target="_blank" rel="noreferrer">
                <span className={styles.link}>Privacy Policy</span>
              </a>
              {` and`}
            </Checkbox>
            <span
              className={styles.link}
              onClick={toggleModalOpen(IntakeFormModalType.Health, true)}
            >
              Health Data Processing
            </span>
            ?
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col span={24}>
          <Form.Item rules={[{ required: true }]}>
            <Checkbox
              onClick={handleClickSMSConsentCheckbox}
              checked={isSMSConsentChecked}
              className={styles.checkbox}
            >
              Have you read and agreed with our
            </Checkbox>
            <span
              className={styles.link}
              onClick={toggleModalOpen(IntakeFormModalType.SMSConsent, true)}
            >
              SMS Communication Consent
            </span>
            ?
          </Form.Item>
        </Col>
      </Row>

      <div className={styles.submitContainer}>
        <Button
          className={cs(styles.button, styles.cancelButton)}
          type="primary"
          onClick={onCancelClick}
          loading={isLoading}
        >
          Cancel
        </Button>

        <Button
          className={styles.button}
          type="default"
          onClick={requestCode}
          disabled={isTimerActive}
        >
          {isTimerActive ? (
            <CountdownTimer
              initialTime={120}
              isActive={isTimerActive}
              onComplete={() => setIsTimerActive(false)}
              renderContent={(counter) => `Resend code (${counter}s)`}
            />
          ) : (
            'Get code'
          )}
        </Button>
        <Button
          className={styles.submitButton}
          type="primary"
          icon={<ArrowRight />}
          iconPosition="end"
          loading={isLoading}
          disabled={!isPrivacyAndHealthChecked || !isSMSConsentChecked}
          onClick={handleFormSubmit}
        >
          Submit
        </Button>
      </div>

      <DialogModal
        isOpen={isHealthModalOpen}
        type={DialogModalTypes.OK}
        title="Health Data Processing"
        onClose={toggleModalOpen(IntakeFormModalType.Health, false)}
        onConfirm={toggleModalOpen(IntakeFormModalType.Health, false)}
        description={
          <div className={styles.descriptionWrapper}>
            <p>
              In accordance with the current provisions on the protection of
              personal data, I give my consent to the processing of health
              information I have submitted, in accordance with current
              legislation, to carry out the following actions with all the
              specified personal data: collection, recording, organization,
              storage, restoration, adaptation or modification, extraction,
              consultation, use, disclosure through transfer, distribution or
              other access, grouping or combining, blocking, erasing or
              destroying.
            </p>
            <p>
              I acknowledge that these actions will be performed in compliance
              with relevant data protection laws. I also understand that my data
              will only be used for the purposes specified above and that I may
              withdraw my consent at any time, without affecting my rights.
            </p>
          </div>
        }
      />

      <DialogModal
        isOpen={isSMSConsentModalOpen}
        type={DialogModalTypes.Confirm}
        title="SMS Communication Consent"
        onClose={toggleModalOpen(IntakeFormModalType.SMSConsent, false)}
        onConfirm={handleSMSConsentModalConfirm}
        description={
          <div className={styles.descriptionWrapper}>
            <p>
              By clicking the “SUBMIT” button and submitting this form, I affirm
              that I have read and agree to this Site’s Terms & Conditions and
              Privacy Policy. I consent to receive SMS text messages to my cell
              number provided above for notifications, alerts, and general
              communication purposes including promotions from Joblio Inc. I
              understand that I am not required to provide my consent as a
              condition of purchasing any products or services. I understand
              that I can opt out of receiving text messages at any time by
              responding with STOP. I can reply with HELP to get help. Message
              and data rates may apply depending on your mobile carrier. Message
              frequency may vary.
            </p>
          </div>
        }
      />
    </>
  );
};

export default IntakeForm;
